/**
 * The Connect wizard's "choices so far" line (equipment · gateway · device ·
 * protocol · points), shown in the sticky step footer. Each item names the
 * step that set it, so a click goes back there. Pure, so it is unit-tested.
 */

import { CONNECT_STEP_ORDER, type ConnectStepId } from "./steps";

export interface ConnectChoices {
  equipment?: string | null;
  gateway?: string | null;
  device?: string | null;
  protocol?: string | null;
  /** Points / registers picked on the Points step. */
  points?: number;
}

export interface SummaryItem {
  key: keyof ConnectChoices;
  /** Short label for screen readers and the chip's title ("Equipment"). */
  label: string;
  value: string;
  /** The step that sets it (a click goes there). */
  step: ConnectStepId;
}

/**
 * The choices made before `current`, in step order. A choice made on the
 * current step or a later one is left out: the line is where you came from.
 */
export function connectSummary(c: ConnectChoices, current: ConnectStepId): SummaryItem[] {
  const at = CONNECT_STEP_ORDER.indexOf(current);
  const before = (s: ConnectStepId) => CONNECT_STEP_ORDER.indexOf(s) < at;
  const items: SummaryItem[] = [];
  const add = (key: keyof ConnectChoices, label: string, value: string | null | undefined, step: ConnectStepId) => {
    if (value && value.trim() && before(step)) items.push({ key, label, value: value.trim(), step });
  };
  add("equipment", "Equipment", c.equipment, "equipment");
  add("gateway", "Gateway", c.gateway, "discover");
  add("device", "Device", c.device, "discover");
  add("protocol", "Protocol", c.protocol, "protocol");
  if (c.points && c.points > 0) add("points", "Points", `${c.points} point${c.points === 1 ? "" : "s"}`, "registers");
  return items;
}

/** A step the operator may jump back to from `current`: any earlier step. */
export function canJumpTo(target: ConnectStepId, current: ConnectStepId): boolean {
  return CONNECT_STEP_ORDER.indexOf(target) < CONNECT_STEP_ORDER.indexOf(current);
}

/**
 * Whether Enter in this element should advance the wizard: a plain text or
 * number field that is not inside its own form (the Discover scan form submits
 * on Enter) and is not a combobox (Enter picks a suggestion there).
 */
export function enterAdvances(el: {
  tagName: string;
  type?: string;
  closest?: (sel: string) => unknown;
  getAttribute?: (name: string) => string | null;
}): boolean {
  if (el.tagName !== "INPUT") return false;
  const type = (el.type ?? "text").toLowerCase();
  if (!["text", "number", "search", "url", "tel", ""].includes(type)) return false;
  if (el.getAttribute?.("role") === "combobox" || el.getAttribute?.("aria-autocomplete")) return false;
  if (el.closest?.("form, [data-no-advance]")) return false;
  return true;
}
